import {DialogFooter, Separator} from 'platform/components';
import {Box, HStack, VStack} from 'platform/foundation';
import styled from 'styled-components';

import {SkeletonLoadingBox} from '@dms/shared';

import type {RequiredTestIdProps} from 'shared/web';

import {MAX_VISIBLE_THUMBNAILS} from '../constants/MAX_VISIBLE_THUMBNAILS';
import {PREVIEW_CONTAINER_BREAKPOINTS} from '../constants/PREVIEW_CONTAINER_BREAKPOINTS';
import {getPreviewContainerBreakpoint} from '../utils/getPreviewContainerBreakpoint';
import {
  Columns,
  DescriptionRow,
  GalleryColumn,
  Hero,
  ParametersRow,
  SpecificationsRow,
  SummaryColumn,
} from './AdvertisementPreviewContent';
import {ThumbnailRow} from './AdvertisementPreviewGallery';
import {
  KeyParametersGrid,
  KeyParameterIconSlot,
  KeyParameterText,
} from './AdvertisementPreviewKeyParameters';
import {SectionCard, SectionCardContent, SectionCardHeader} from './PreviewSectionCard';

const KEY_PARAMETER_SKELETON_COUNT = 6;
const SPECIFICATION_GROUP_COUNT = 6;
const SPECIFICATION_ROWS_PER_GROUP = 4;
const FEATURE_GROUP_COUNT = 4;
const FEATURE_ROWS_PER_GROUP = 4;
const DESCRIPTION_LINE_COUNT = 7;

export function AdvertisementPreviewSkeleton(props: RequiredTestIdProps) {
  return (
    <SkeletonShell data-testid={props['data-testid']}>
      <VStack>
        <PlatformStrip>
          <HStack spacing={2} wrap="nowrap">
            {([30, 24, 28, 26] as const).map((width) => (
              <SkeletonLoadingBox
                key={width}
                isLoading
                width={width}
                height={8}
                borderRadius="medium"
              />
            ))}
          </HStack>
        </PlatformStrip>
        <PreviewCanvas>
          <ToolbarSkeleton>
            <SkeletonLoadingBox isLoading width={42} height={4} />
            <HStack spacing={3} align="center">
              <SkeletonLoadingBox isLoading width={4} height={4} />
              <SkeletonLoadingBox isLoading width={8} height={4} />
            </HStack>
          </ToolbarSkeleton>
          <Columns>
            <SectionNavSkeleton>
              {[0, 1, 2, 3].map((index) => (
                <NavItemSkeleton key={index}>
                  <SkeletonLoadingBox isLoading width={4} height={4} />
                  <SkeletonLoadingBox isLoading width={index === 2 ? 20 : 16} height={4} />
                </NavItemSkeleton>
              ))}
            </SectionNavSkeleton>
            <Hero>
              <GalleryColumn>
                <VStack spacing={2} align="stretch">
                  <PhotoSkeleton data-testid={`${props['data-testid']}-main-photo-skeleton`}>
                    <SkeletonFill>
                      <SkeletonLoadingBox isLoading borderRadius="small" />
                    </SkeletonFill>
                  </PhotoSkeleton>
                  <ThumbnailRow>
                    {Array.from({length: MAX_VISIBLE_THUMBNAILS}, (_, index) => (
                      <PhotoSkeleton
                        key={index}
                        data-testid={`${props['data-testid']}-thumbnail-skeleton-${index}`}
                      >
                        <SkeletonFill>
                          <SkeletonLoadingBox isLoading borderRadius="small" />
                        </SkeletonFill>
                      </PhotoSkeleton>
                    ))}
                  </ThumbnailRow>
                </VStack>
              </GalleryColumn>
              <SummaryColumn>
                <VStack spacing={2} align="stretch">
                  <Box width="85%">
                    <SkeletonLoadingBox isLoading height={8} />
                  </Box>
                  <Box width="65%">
                    <SkeletonLoadingBox isLoading height={6} />
                  </Box>
                </VStack>
                <PriceSkeletonPanel>
                  <SkeletonLoadingBox isLoading width={24} height={4} />
                  <Box width="75%">
                    <SkeletonLoadingBox isLoading height={8} />
                  </Box>
                  <Separator spacing={2} />
                  <SkeletonLoadingBox isLoading width={28} height={4} />
                  <Box width="60%">
                    <SkeletonLoadingBox isLoading height={6} />
                  </Box>
                </PriceSkeletonPanel>
              </SummaryColumn>
              <ParametersRow>
                <KeyParametersGrid>
                  {Array.from({length: KEY_PARAMETER_SKELETON_COUNT}, (_, index) => (
                    <Box key={index} padding={1} minWidth={0}>
                      <HStack align="flex-start" spacing={2} minWidth={0}>
                        <KeyParameterIconSlot aria-hidden="true">
                          <SkeletonLoadingBox isLoading width={6} height={6} />
                        </KeyParameterIconSlot>
                        <KeyParameterText>
                          <Box width="80%">
                            <SkeletonLoadingBox isLoading height={4} />
                          </Box>
                          <Box width="95%">
                            <SkeletonLoadingBox isLoading height={5} />
                          </Box>
                        </KeyParameterText>
                      </HStack>
                    </Box>
                  ))}
                </KeyParametersGrid>
              </ParametersRow>
            </Hero>
            <SpecificationsRow>
              <SectionCard>
                <SectionHeadingSkeleton>
                  <SkeletonLoadingBox isLoading width={5} height={5} />
                  <SkeletonLoadingBox isLoading width={40} height={6} />
                </SectionHeadingSkeleton>
                <SpecificationsGridSkeleton>
                  {Array.from({length: SPECIFICATION_GROUP_COUNT}, (_, group) => (
                    <SpecificationGroupSkeleton key={group}>
                      <HStack spacing={2} align="center">
                        <SkeletonLoadingBox isLoading width={4} height={4} />
                        <Box width="55%">
                          <SkeletonLoadingBox isLoading height={4} />
                        </Box>
                      </HStack>
                      <Separator spacing={3} />
                      {Array.from({length: SPECIFICATION_ROWS_PER_GROUP}, (_, index) => (
                        <HStack key={index} spacing={3} justify="space-between">
                          <Box width="40%">
                            <SkeletonLoadingBox isLoading height={3} />
                          </Box>
                          <Box width="35%">
                            <SkeletonLoadingBox isLoading height={3} />
                          </Box>
                        </HStack>
                      ))}
                    </SpecificationGroupSkeleton>
                  ))}
                </SpecificationsGridSkeleton>
              </SectionCard>
            </SpecificationsRow>
            <SectionCard>
              <SectionHeadingSkeleton>
                <SkeletonLoadingBox isLoading width={36} height={6} />
                <SkeletonLoadingBox isLoading width={16} height={4} />
              </SectionHeadingSkeleton>
              <FeaturesGridSkeleton>
                {Array.from({length: FEATURE_GROUP_COUNT}, (_, group) => (
                  <VStack key={group} spacing={3} align="stretch">
                    <Box width="75%">
                      <SkeletonLoadingBox isLoading height={5} />
                    </Box>
                    <Separator spacing={1} />
                    {Array.from({length: FEATURE_ROWS_PER_GROUP}, (_, index) => (
                      <HStack key={index} spacing={2} align="center">
                        <SkeletonLoadingBox isLoading width={4} height={4} />
                        <Box width="75%">
                          <SkeletonLoadingBox isLoading height={4} />
                        </Box>
                      </HStack>
                    ))}
                  </VStack>
                ))}
              </FeaturesGridSkeleton>
            </SectionCard>
            <DescriptionRow>
              <SectionCard>
                <SectionCardHeader>
                  <HStack spacing={2} align="center">
                    <SkeletonLoadingBox isLoading width={5} height={5} />
                    <SkeletonLoadingBox isLoading width={36} height={6} />
                  </HStack>
                  <SkeletonLoadingBox isLoading width={28} height={8} />
                </SectionCardHeader>
                <SectionCardContent>
                  <VStack spacing={2} align="stretch">
                    {Array.from({length: DESCRIPTION_LINE_COUNT}, (_, index) => (
                      <Box
                        key={index}
                        width={index === DESCRIPTION_LINE_COUNT - 1 ? '70%' : '100%'}
                      >
                        <SkeletonLoadingBox isLoading height={4} />
                      </Box>
                    ))}
                  </VStack>
                </SectionCardContent>
              </SectionCard>
            </DescriptionRow>
          </Columns>
        </PreviewCanvas>
        <DialogFooter>
          <FooterSkeleton>
            <SkeletonLoadingBox isLoading width={56} height={4} />
            <HStack justify="flex-end" spacing={2}>
              <SkeletonLoadingBox isLoading width={20} height={10} borderRadius="medium" />
              <SkeletonLoadingBox isLoading width={24} height={10} borderRadius="medium" />
            </HStack>
          </FooterSkeleton>
        </DialogFooter>
      </VStack>
    </SkeletonShell>
  );
}

const SkeletonShell = styled.div`
  container: preview-dialog / inline-size;
  min-width: 0;
`;
const PlatformStrip = styled.div`
  padding: ${({theme}) => theme.getSize(3)} ${({theme}) => theme.getSize(7)};
  background: ${({theme}) => theme.colors.palettes.white[10][100]};
  border-bottom: 1px solid ${({theme}) => theme.colors.general.separator};
  @container preview-dialog (max-width: ${getPreviewContainerBreakpoint(
    PREVIEW_CONTAINER_BREAKPOINTS.dialogMedium
  )}) {
    padding-inline: ${({theme}) => theme.getSize(4)};
  }
  @container preview-dialog (max-width: ${getPreviewContainerBreakpoint(
    PREVIEW_CONTAINER_BREAKPOINTS.dialogCompact
  )}) {
    padding: ${({theme}) => theme.getSize(3)};
  }
`;
const PreviewCanvas = styled.div`
  padding: 0 ${({theme}) => theme.getSize(7)} ${({theme}) => theme.getSize(7)};
  background: ${({theme}) => theme.colors.palettes.neutral[10][100]};
  @container preview-dialog (max-width: ${getPreviewContainerBreakpoint(
    PREVIEW_CONTAINER_BREAKPOINTS.dialogMedium
  )}) {
    padding-inline: ${({theme}) => theme.getSize(4)};
  }
  @container preview-dialog (max-width: ${getPreviewContainerBreakpoint(
    PREVIEW_CONTAINER_BREAKPOINTS.dialogCompact
  )}) {
    padding-inline: ${({theme}) => theme.getSize(3)};
  }
`;
const ToolbarSkeleton = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: ${({theme}) => theme.getSize(3)};
  padding: ${({theme}) => theme.getSize(4)} 0 ${({theme}) => theme.getSize(3)};
`;
const SectionNavSkeleton = styled.div`
  position: sticky;
  top: ${({theme}) => theme.getSize(12)};
  z-index: 10;
  display: flex;
  flex-wrap: nowrap;
  overflow-x: hidden;
  gap: 0 ${({theme}) => theme.getSize(4)};
  padding: 0 ${({theme}) => theme.getSize(4)};
  border: 1px solid ${({theme}) => theme.colors.general.separator};
  border-radius: ${({theme}) => theme.radii.medium};
  background: ${({theme}) => theme.colors.palettes.white[10][100]};
  @container preview (max-width: ${getPreviewContainerBreakpoint(
    PREVIEW_CONTAINER_BREAKPOINTS.medium
  )}) {
    display: grid;
    grid-template-columns: repeat(4, minmax(0, 1fr));
    gap: 0;
    padding: 0;
  }
`;
const NavItemSkeleton = styled.div`
  flex-shrink: 0;
  display: flex;
  align-items: center;
  gap: ${({theme}) => theme.getSize(2)};
  padding: ${({theme}) => theme.getSize(3)} ${({theme}) => theme.getSize(1)};
  @container preview (max-width: ${getPreviewContainerBreakpoint(
    PREVIEW_CONTAINER_BREAKPOINTS.medium
  )}) {
    min-width: 0;
    flex-direction: column;
    justify-content: center;
    gap: ${({theme}) => theme.getSize(1)};
    padding: ${({theme}) => theme.getSize(2)} ${({theme}) => theme.getSize(1)};
  }
`;
const PriceSkeletonPanel = styled.div`
  display: grid;
  gap: ${({theme}) => theme.getSize(4)};
  padding: ${({theme}) => theme.getSize(5)};
  border-radius: ${({theme}) => theme.radii.medium};
  background: ${({theme}) => theme.colors.palettes.neutral[20][100]};
  @container preview (max-width: ${getPreviewContainerBreakpoint(
    PREVIEW_CONTAINER_BREAKPOINTS.wide
  )}) {
    padding: ${({theme}) => theme.getSize(4)};
  }
  @container preview (max-width: ${getPreviewContainerBreakpoint(
    PREVIEW_CONTAINER_BREAKPOINTS.compact
  )}) {
    gap: ${({theme}) => theme.getSize(3)};
  }
`;
const SectionHeadingSkeleton = styled.div`
  display: flex;
  align-items: center;
  gap: ${({theme}) => theme.getSize(2)};
  padding: ${({theme}) => theme.getSize(6)} ${({theme}) => theme.getSize(6)} 0;
  @container preview (max-width: ${getPreviewContainerBreakpoint(
    PREVIEW_CONTAINER_BREAKPOINTS.compact
  )}) {
    padding: ${({theme}) => theme.getSize(4)} ${({theme}) => theme.getSize(4)} 0;
  }
`;
const SpecificationsGridSkeleton = styled.div`
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: ${({theme}) => theme.getSize(4)};
  padding: ${({theme}) => theme.getSize(5)} ${({theme}) => theme.getSize(6)}
    ${({theme}) => theme.getSize(6)};
  @container preview (max-width: ${getPreviewContainerBreakpoint(
    PREVIEW_CONTAINER_BREAKPOINTS.tablet
  )}) {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
  @container preview (max-width: ${getPreviewContainerBreakpoint(
    PREVIEW_CONTAINER_BREAKPOINTS.phone
  )}) {
    grid-template-columns: minmax(0, 1fr);
    padding: ${({theme}) => theme.getSize(4)};
  }
`;
const SpecificationGroupSkeleton = styled.div`
  display: grid;
  gap: ${({theme}) => theme.getSize(2)};
  min-width: 0;
  align-self: start;
  padding: ${({theme}) => theme.getSize(4)};
  border-radius: ${({theme}) => theme.radii.medium};
  background: ${({theme}) => theme.colors.palettes.neutral[20][100]};
`;
const FeaturesGridSkeleton = styled.div`
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: ${({theme}) => theme.getSize(7)};
  padding: ${({theme}) => theme.getSize(5)} ${({theme}) => theme.getSize(6)}
    ${({theme}) => theme.getSize(6)};
  @container preview (max-width: ${getPreviewContainerBreakpoint(
    PREVIEW_CONTAINER_BREAKPOINTS.wide
  )}) {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
  @container preview (max-width: ${getPreviewContainerBreakpoint(
    PREVIEW_CONTAINER_BREAKPOINTS.phone
  )}) {
    grid-template-columns: minmax(0, 1fr);
  }
`;
const FooterSkeleton = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${({theme}) => theme.getSize(4)};
  @container preview-dialog (max-width: ${getPreviewContainerBreakpoint(
    PREVIEW_CONTAINER_BREAKPOINTS.dialogCompact
  )}) {
    flex-wrap: wrap;
    justify-content: flex-end;
  }
`;
const PhotoSkeleton = styled.div`
  position: relative;
  aspect-ratio: 4 / 3;
  min-width: 0;
`;
const SkeletonFill = styled.div`
  position: absolute;
  inset: 0;
  display: grid;
  grid-template-rows: minmax(0, 1fr);
`;
